// Dependencies
"use strict";
Object.defineProperty(exports, "__esModule", {
    value: true
});
function _export(target, all) {
    for(var name in all)Object.defineProperty(target, name, {
        enumerable: true,
        get: Object.getOwnPropertyDescriptor(all, name).get
    });
}
_export(exports, {
    get CohortModel () {
        return CohortModel;
    },
    get CohortQueryModel () {
        return CohortQueryModel;
    },
    get CustomQuestionGetResponse () {
        return CustomQuestionGetResponse;
    },
    get CustomQuestionModel () {
        return CustomQuestionModel;
    },
    get EngagementDashboardUserSettingsModel () {
        return EngagementDashboardUserSettingsModel;
    },
    get EngagementSettingsFormSchema () {
        return EngagementSettingsFormSchema;
    },
    get EngagementSettingsSchema () {
        return EngagementSettingsSchema;
    },
    get EngagementSurveyFormModel () {
        return EngagementSurveyFormModel;
    },
    get EngagementSurveyFormModelByQuestionID () {
        return EngagementSurveyFormModelByQuestionID;
    },
    get EngagementSurveyFormModelQuestion () {
        return EngagementSurveyFormModelQuestion;
    },
    get EngagementSurveyModel () {
        return EngagementSurveyModel;
    },
    get Field () {
        return Field;
    },
    get FieldComparator () {
        return FieldComparator;
    },
    get QuestionModel () {
        return QuestionModel;
    }
});
const _bson = require("bson");
const _zod = require("zod");
const RelativeDateFieldOperator = _zod.z.object({
    relative_date: _zod.z.number(),
    relative_date_unit: _zod.z.enum([
        "days",
        "years",
        "months"
    ]),
    timezone: _zod.z.string().optional()
});
const AnniversaryDateFieldOperator = _zod.z.object({
    anniversary_date: _zod.z.number(),
    anniversary_date_unit: _zod.z.enum([
        "days",
        "months"
    ]),
    timezone: _zod.z.string().optional()
});
/**
 * This is the schema for the value of a field in a cohort query that allows
 * for extra operators to be used like date differences.
 */ const FieldOperator = _zod.z.union([
    RelativeDateFieldOperator,
    AnniversaryDateFieldOperator,
    _zod.z.object({
        date: _zod.z.string()
    }),
    _zod.z.object({
        oid: _zod.z.string()
    }),
    _zod.z.string(),
    _zod.z.number(),
    _zod.z.null(),
    _zod.z.boolean()
]);
const FieldComparator = _zod.z.enum([
    "eq",
    "ne",
    "gt",
    "lt",
    "gte",
    "lte",
    "in",
    "nin",
    "regex",
    "not_regex",
    "eq_anniversary",
    "after_anniversary",
    "before_anniversary",
    // Backwards compatibility
    "gte_anniversary",
    "lte_anniversary"
]);
const Field = _zod.z.union([
    _zod.z.enum([
        "is_manager"
    ]),
    _zod.z.object({
        oid: _zod.z.string()
    })
]);
const CohortQueryModel = _zod.z.object({
    and: _zod.z.array(_zod.z.object({
        or: _zod.z.array(_zod.z.object({
            field: Field,
            comparator: FieldComparator,
            value: FieldOperator
        }))
    }))
});
const CohortModel = _zod.z.object({
    _id: _zod.z.instanceof(_bson.ObjectId),
    company_id: _zod.z.instanceof(_bson.ObjectId),
    name: _zod.z.string(),
    description: _zod.z.string(),
    type: _zod.z.enum([
        "static",
        "dynamic"
    ]),
    employee_ids: _zod.z.array(_zod.z.instanceof(_bson.ObjectId)),
    color: _zod.z.string().regex(/^#[0-9a-f]{6}$/),
    created_at: _zod.z.date(),
    updated_at: _zod.z.date(),
    deleted_at: _zod.z.nullable(_zod.z.date()),
    query: CohortQueryModel.optional(),
    calculation_status: _zod.z.enum([
        "processing",
        "backpropagating",
        "idle"
    ]).optional()
});
const QuestionModel = _zod.z.object({
    id: _zod.z.string().min(1),
    factor: _zod.z.enum([
        "eNPS",
        "Role",
        "Management",
        "Leadership",
        "Organization",
        "Trust",
        "Fit",
        "Opportunity",
        "Custom",
        "Other"
    ]),
    subfactor: _zod.z.string().nullable(),
    question: _zod.z.string(),
    scale: _zod.z.enum([
        "nps",
        "likert5",
        "text"
    ])
});
const CustomQuestionModel = QuestionModel.extend({
    _id: _zod.z.instanceof(_bson.ObjectId),
    company_id: _zod.z.instanceof(_bson.ObjectId),
    factor: _zod.z.enum([
        "Custom"
    ]),
    scale: _zod.z.enum([
        "likert5",
        "text"
    ]),
    created_at: _zod.z.date(),
    created_by: _zod.z.instanceof(_bson.ObjectId),
    updated_at: _zod.z.date(),
    updated_by: _zod.z.instanceof(_bson.ObjectId),
    deleted_at: _zod.z.date().nullable(),
    deleted_by: _zod.z.string().nullable(),
    enabled: _zod.z.boolean(),
    id: _zod.z.undefined()
});
const CustomQuestionGetResponse = CustomQuestionModel.extend({
    updated_by_employee: _zod.z.object({
        _id: _zod.z.instanceof(_bson.ObjectId),
        name: _zod.z.string()
    }),
    created_at: _zod.z.string().datetime(),
    updated_at: _zod.z.string().datetime(),
    deleted_at: _zod.z.string().datetime().nullable()
});
const ReminderEmailSentEntry = _zod.z.object({
    sent_at: _zod.z.date(),
    trigger: _zod.z.enum([
        "manual",
        "automatic"
    ])
});
const EngagementSurveyModel = _zod.z.object({
    _id: _zod.z.instanceof(_bson.ObjectId),
    created_at: _zod.z.date(),
    company_id: _zod.z.instanceof(_bson.ObjectId),
    reminder_emails_sent: _zod.z.union([
        _zod.z.date().nullable(),
        _zod.z.array(ReminderEmailSentEntry)
    ]).optional(),
    survey_status: _zod.z.enum([
        "open",
        "closed",
        "expired"
    ]).optional(),
    closed_at: _zod.z.date().nullable().optional(),
    enps_range: _zod.z.object({
        below: _zod.z.number().nullable().optional(),
        above: _zod.z.number().nullable().optional()
    }).optional()
});
const EngagementSurveyFormModelQuestion = _zod.z.object({
    id: _zod.z.string(),
    question_id: _zod.z.string(),
    index: _zod.z.number(),
    value: _zod.z.nullable(_zod.z.number().int().min(0).max(10).or(_zod.z.string())),
    submitted_at: _zod.z.nullable(_zod.z.date()),
    additional_feedback: _zod.z.string().nullable().optional()
});
const EngagementSurveyFormModel = _zod.z.object({
    _id: _zod.z.instanceof(_bson.ObjectId),
    survey_id: _zod.z.instanceof(_bson.ObjectId),
    company_id: _zod.z.instanceof(_bson.ObjectId),
    access_key: _zod.z.string().length(64),
    respondent: _zod.z.object({
        _id: _zod.z.instanceof(_bson.ObjectId),
        cohorts: _zod.z.array(_zod.z.instanceof(_bson.ObjectId))
    }),
    questions: _zod.z.array(EngagementSurveyFormModelQuestion),
    submitted_at: _zod.z.nullable(_zod.z.date()),
    created_at: _zod.z.date(),
    updated_at: _zod.z.date(),
    deleted_at: _zod.z.nullable(_zod.z.date()),
    dismissed_at: _zod.z.nullable(_zod.z.date()).optional(),
    dismissed: _zod.z.boolean().nullable().optional()
});
const EngagementSurveyFormModelByQuestionID = EngagementSurveyFormModelQuestion.pick({
    id: true,
    question_id: true,
    value: true,
    submitted_at: true,
    index: true,
    additional_feedback: true
});
const EngagementDashboardUserSettingsModel = _zod.z.object({
    _id: _zod.z.instanceof(_bson.ObjectId),
    company_id: _zod.z.instanceof(_bson.ObjectId),
    user_id: _zod.z.instanceof(_bson.ObjectId),
    page_settings: _zod.z.object({
        cohorts: _zod.z.object({
            trendline: _zod.z.object({
                selected_cohorts: _zod.z.array(_zod.z.instanceof(_bson.ObjectId))
            })
        })
    })
});
const EngagementSettingsSchema = _zod.z.object({
    next_survey_date: _zod.z.date().nullish().transform((val)=>val ?? new Date(Date.now() + 24 * 60 * 60 * 1000)),
    last_survey_date: _zod.z.date().optional(),
    last_survey_reminder_date: _zod.z.date().optional(),
    current_survey_status: _zod.z.enum([
        "open",
        "closed",
        "expired"
    ]).nullable(),
    enabled: _zod.z.boolean(),
    frequency: _zod.z.number().min(1).max(364),
    enps_frequency: _zod.z.number().min(0).max(4),
    ask_additional_enps_feedback_question: _zod.z.boolean(),
    enps_range: _zod.z.object({
        below: _zod.z.nullable(_zod.z.number().min(0).max(10)),
        above: _zod.z.nullable(_zod.z.number().min(0).max(10))
    }),
    factors_frequency: _zod.z.number().min(1).max(4),
    survey_time: _zod.z.enum([
        "morning",
        "mid-day",
        "afternoon",
        "evening"
    ]),
    question_cycle: _zod.z.number().min(1),
    custom_questions_frequency: _zod.z.number().min(0).max(4),
    custom_questions_include_all: _zod.z.boolean(),
    send_reminder_emails: _zod.z.boolean(),
    disabled_questions: _zod.z.array(_zod.z.string()),
    additional_feedback_question_frequency: _zod.z.number().min(0).max(4),
    employee_status_for_surveys: _zod.z.enum([
        "active",
        "invited"
    ]),
    excluded_users_bulk_selection_id: _zod.z.string().optional(),
    selection_method: _zod.z.enum([
        "automatic",
        "manual"
    ]).default("automatic"),
    manually_selected_questions: _zod.z.array(_zod.z.object({
        question_id: _zod.z.string(),
        order: _zod.z.number()
    })).default([]),
    in_email_answering: _zod.z.boolean().default(true)
});
const EngagementSettingsFormSchema = _zod.z.object({
    nextSurveyDate: _zod.z.date().optional(),
    lastSurveyDate: _zod.z.date().optional(),
    enabled: EngagementSettingsSchema.shape.enabled,
    surveyFrequency: EngagementSettingsSchema.shape.frequency,
    enpsFrequency: EngagementSettingsSchema.shape.enps_frequency,
    factorsFrequency: EngagementSettingsSchema.shape.factors_frequency,
    surveyTime: EngagementSettingsSchema.shape.survey_time,
    questionCycle: EngagementSettingsSchema.shape.question_cycle,
    sendReminderEmails: EngagementSettingsSchema.shape.send_reminder_emails,
    customQuestionsFrequency: EngagementSettingsSchema.shape.custom_questions_frequency,
    customQuestionsIncludeAll: EngagementSettingsSchema.shape.custom_questions_include_all,
    additionalFeedbackQuestionFrequency: EngagementSettingsSchema.shape.additional_feedback_question_frequency,
    in_email_answering: _zod.z.boolean().optional().default(true)
});
